<template>
    <div id="sidebar" class="column col-4 shadow p-2">
        <div>
            <h2>{{ title }}</h2>

            <div id="uploadSidebar" v-if="isCategory('upload')">
                <p>
                    Please upload the required clinical photographs and
                    provide the height of the right upper central incisor
                    (incisal edge to zenith).
                </p>

                <p>
                    <i class="icon" :class="getExtraOralIcon"></i>
                    Extraoral photo, full smile.
                </p>

                <p>
                    <i class="icon" :class="getIntraOralIcon"></i>
                    Intraoral photo, retractors and reflector.
                </p>

                <div class="columns">
                    <div class="column col-auto">
                        <label class="form-label" for="input-height">
                            <i class="icon" :class="getReferenceLengthIcon"></i>
                            Height of tooth 11
                        </label>
                    </div>
                    <div class="column">
                        <div class="input-group">
                            <input v-model.number="referenceLength"
                                   type="number" min="1" max="50"
                                   class="form-input" id="input-height" placeholder="e.g. 9">
                            <span class="input-group-addon">mm</span>
                        </div>
                    </div>
                </div>
            </div>

            <div id="alignSidebar" v-if="isCategory('align')">
                <p v-if="state.matches('alignExtraOral')">
                    Move and rotate the extraoral photograph so that the horizontal line overlaps the
                    pupils and the vertical line corresponds to the facial midline.
                </p>

                <p v-if="state.matches('alignIntraOral')">
                    Then place the intraoral photograph over the extraoral photo so that the incisal
                    edges overlap.
                </p>

                <p v-if="state.matches('alignReferenceLine')">
                    Move the horizontal reference line (dashed) so that it is located just below
                    the incisal edge of the upper central incisors.
                </p>
            </div>

            <div id="drawSidebar" v-if="isCategory('draw')">
                <template v-if="state.matches('drawContour')">
                    <p>Select the tooth</p>
                    <div class="btn-group btn-group-block">
                        <button v-for="(number, index) in toothNumbers" :key="number"
                                class="btn"
                                :class="getContourStatus(index)"
                                @click="activeContour = index">
                            {{ number }}
                        </button>
                    </div>

                    <div>
                        <button class="btn btn-link mt-2" @click="deleteActiveContour">
                            <i class="icon icon-delete"></i>
                            Delete contour {{ toothNumbers[activeContour] }}
                        </button>
                    </div>

                    <div>
                        <button class="btn btn-link mt-1" @click="deleteAllContours">
                            <i class="icon icon-delete"></i>
                            Delete all contours
                        </button>
                    </div>
                </template>
            </div>

            <div id="designSidebar" v-if="isCategory('design')">
                <div v-if="state.matches('design')">

                </div>
            </div>
        </div>

        <div class="columns">
            <div class="column col-6 col-mr-auto">
                <button class="btn btn-lg" :disabled="prevDisabled" v-on:click="previousState">Previous</button>
            </div>
            <div class="column col-6 text-right">
                <button class="btn btn-lg btn-primary" :disabled="nextDisabled" v-on:click="nextState">Next</button>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        computed: {
            /*
             * State computed methods
             */
            state () {
                return this.$store.state.currentState
            },

            title () {
                return this.$store.getters.stateTitle
            },

            prevDisabled () {
                return !this.state.nextEvents.includes('PREV')
            },

            nextDisabled () {
                if (!this.state.nextEvents.includes('NEXT')) {
                    return true
                }

                if (this.state.matches('upload')) {
                    // both images need to be uploaded
                    // and the ruler needs to be calibrated
                    return !this.$store.getters.intraOralImageLoaded
                        || !this.$store.getters.extraOralImageLoaded
                        || !this.$store.getters.rulerCalibrated
                } else if (this.state.matches('drawZenith')) {
                    return this.$store.state.zenithCoords.length < 6
                } else if (this.state.matches('drawIncisalEdge')) {
                    return this.$store.state.incisalEdgeCoords.length < 6
                } else if (this.state.matches('drawContour')) {
                    return this.$store.state.contourCoords.some((points) => points.length < 3)
                }

                return false
            },


            getExtraOralIcon () {
                return {
                    'icon-arrow-right': !this.$store.getters.extraOralImageLoaded,
                    'icon-check': this.$store.getters.extraOralImageLoaded,
                    'text-success': this.$store.getters.extraOralImageLoaded
                }
            },

            getIntraOralIcon () {
                return {
                    'icon-arrow-right': !this.$store.getters.intraOralImageLoaded,
                    'icon-check': this.$store.getters.intraOralImageLoaded,
                    'text-success': this.$store.getters.intraOralImageLoaded
                }
            },


            // Reference length
            referenceLength: {
                get () {
                    return this.$store.state.referenceLength
                },
                set (value) {
                    this.$store.commit('updateReferenceLength', value)
                }
            },

            getReferenceLengthIcon () {
                return {
                    'icon-arrow-right': !this.$store.getters.rulerCalibrated,
                    'icon-check': this.$store.getters.rulerCalibrated,
                    'text-success': this.$store.getters.rulerCalibrated
                }
            },

            // Active contour
            activeContour: {
                get () {
                    return this.$store.state.activeContour
                },
                set (value) {
                    this.$store.commit('updateActiveContour', value)
                }
            },
        },
        data () {
            return {
                toothNumbers: [ 13, 12, 11, 21, 22, 23 ],
            }
        },
        name: "Sidebar",
        methods: {
            isCategory(name) {
                return this.$store.getters.isCategory(name)
            },

            previousState() {
                this.$store.commit("transition", "PREV")
            },

            nextState() {
                this.$store.commit("transition", "NEXT")
            },

            deleteActiveContour() {
                this.$store.commit("deleteContour", this.activeContour)
            },

            deleteAllContours() {
                this.$store.commit("deleteAllContours")
            },

            getContourStatus(index) {
                return {
                    'btn-primary': this.activeContour === index
                }
            }
        }
    }
</script>

<style scoped lang="scss">
#sidebar {
    height: 565px;

    display:flex;
    flex-direction: column;
    justify-content: space-between;

    h2 {
        text-align: center;
    }
}
</style>